<template>
  <div>
    <validation-observer
      v-slot="bicycleLaptopFormObserver"
      slim
    >
      <form-section-highlight>
        <form-row :label="label" />

        <!-- ITEM TYPE -->
        <form-row
          :id="`content_${uniqueId}_bicyclelaptop_property-bicycle-or-laptop`"
          question="What type of item is it?"
        >
          <validation-provider
            v-slot="{ failed }"
            name="Property Bicycle Or Laptop"
            :vid="`content_${uniqueId}_bicyclelaptop_property-bicycle-or-laptop`"
            rules="required"
            slim
          >
            <radio
              v-model="bicycleLaptopItem.propertyBicycleOrLaptop"
              :name="`${uniqueId}-type-of-item-bicycle-laptop`"
              wide
              data-test="property_bicycle_or_laptop"
              :error="failed"
              :items="options.propertyBicycleOrLaptopItems"
            />
          </validation-provider>
        </form-row>

        <!-- ITEM DESCRIPTION -->
        <form-row
          :id="`content_${uniqueId}_bicyclelaptop_property-bicycle-laptop-description`"
          question="Please enter a short description for this item"
        >
          <validation-provider
            v-slot="{ errors, failed }"
            name="Property Bicycle Laptop Description"
            :vid="`content_${uniqueId}_bicyclelaptop_property-bicycle-laptop-description`"
            rules="propertyInsuredItemDescription"
            slim
          >
            <qz-textarea
              v-model.trim="bicycleLaptopItem.propertyBicycleLaptopDescription"
              :error="failed"
              data-test="property_bicycle_laptop_description"
              :maxlength="inputCharsLimiters.ITEM_DESCRIPTION_MAX"
              :error-messages="errors"
              :placeholder="placeholder"
              @keydown.enter.prevent
              @keyup="applyCharLimiterAndRemoveNewLines"
            />
          </validation-provider>
        </form-row>

        <!-- ITEM VALUE -->
        <form-row
          :id="`content_${uniqueId}_bicyclelaptop_property-bicycle-laptop-value`"
          question="Please give a value to this item"
        >
          <validation-provider
            v-slot="{ errors, failed }"
            name="Property Bicycle Laptop Value"
            :vid="`content_${uniqueId}_bicyclelaptop_property-bicycle-laptop-value`"
            rules="propertyBicycleLaptopValue"
            slim
          >
            <currency-input
              v-model.number="bicycleLaptopItem.propertyBicycleLaptopValue"
              hide-details="auto"
              outlined
              placeholder="e.g. 1000"
              :error="failed"
              data-test="property_bicycle_laptop_value"
              :maxlength="inputCharsLimiters.INSURED_ITEM_MAX_VALUE"
              :error-messages="errors"
              prefix="£"
            />
          </validation-provider>
        </form-row>

        <!-- ITEM COVER -->
        <form-row
          :id="`content_${uniqueId}_bicyclelaptop_property-bicycle-laptop-outside`"
          question="Would you like to add cover to this item
for outside the home?"
        >
          <validation-provider
            v-slot="{ failed }"
            name="Property Bicycle Laptop Outside"
            :vid="`content_${uniqueId}_bicyclelaptop_property-bicycle-laptop-outside`"
            rules="required"
            slim
          >
            <radio
              v-model="bicycleLaptopItem.propertyBicycleLaptopOutside"
              :name="`${uniqueId}-property-bicycle-laptop-outside`"
              wide
              data-test="property_bicycle_laptop_outside"
              :error="failed"
              :items="options.yesNo"
            />
          </validation-provider>
        </form-row>

        <!-- SUBMIT BTN -->
        <div class="row no-gutters justify-end mt-4">
          <qz-btn
            depressed
            x-large
            dark
            rounded
            color="navy-light"
            data-test="save_details_bicycle_laptop_btn"
            @click="saveItem(bicycleLaptopFormObserver)"
          >
            Save details
            <v-icon right>
              mdi-content-save
            </v-icon>
          </qz-btn>
          <qz-btn
            color="primary"
            text
            rounded
            class="ml-2"
            x-large
            data-test="cancel_edit_bicycle_laptop_btn"
            @click.prevent="clearItem"
          >
            Cancel
            <template v-if="isEdit">
              edit
            </template>
          </qz-btn>
        </div>
      </form-section-highlight>
    </validation-observer>

    <!-- HIDDEN FIELD TO CHECK EDITOR NOT OPENED -->
    <validation-provider
      v-slot="{ failed }"
      name="Form Open Bicycle Laptop"
      vid="content_form-open-bicycle-laptop"
      :rules="{ is_not: { other: true } }"
    >
      <input
        :value="formOpen"
        type="hidden"
      >
      <div
        v-if="failed && formObserver.validated"
        class="error--text text-body-1 mt-4"
      >
        You have unsaved details. Please save or cancel these details before continuing.
      </div>
    </validation-provider>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';

import getStepOptions from '@/helpers/home/getStepOptions';

export default {
  name: 'form-bicycle-laptop',
  props: {
    uniqueId: {
      type: String,
      default: 'new',
    },
    index: {
      type: Number,
      default: null,
    },
    formObserver: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  inject: [
    'inputCharsLimiters',
    'helpers',
  ],
  data () {
    return {
      formOpen: true,

      bicycleLaptopItem: cloneDeep(this.item),
    };
  },
  computed: {
    label () {
      return this.helpers.getSubformLabel('Bicycle/Laptop', this.isEdit, this.index);
    },
    placeholder () {
      const commonText = 'Enter a description for only one';
      return this.bicycleLaptopItem.propertyBicycleOrLaptop === 'L' ? `e.g. 13 inch MacBook. ${commonText} Laptop.` : `e.g. Race Bike. ${commonText} Bicycle.`;
    },
  },
  methods: {
    async saveItem (observer) {
      const isValid = await observer.validate();

      if (!isValid) {
        const failedField = Object.values(observer.fields).find(field => field.invalid).id;

        this.$scrollTo(`#${failedField}`);
        return;
      }

      this.$emit('save', this.bicycleLaptopItem);
    },
    clearItem () {
      this.$emit('clear');
    },
    applyCharLimiterAndRemoveNewLines () {
      this.helpers.limitInputChars(this.bicycleLaptopItem, 'propertyBicycleLaptopDescription', this.inputCharsLimiters.ITEM_DESCRIPTION_MAX);
      this.helpers.removeNewLineChars(this.bicycleLaptopItem, 'propertyBicycleLaptopDescription');
    },
  },
  setup () {
    return {
      options: getStepOptions(2),
    };
  },
};
</script>
